import { 
  Container,
  VStack, 
  Heading, 
  useToast, 
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Box
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { Link as RouterLink } from 'react-router-dom'
import DataTable from '../../../common/DataTable/DataTable'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from '../../../../utils/apiClient'

const ApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([])
  const [filteredApiKeys, setFilteredApiKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const toast = useToast()
  const navigate = useNavigate()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0]

  // Enhanced columns with better formatting
  const columns = [
    { 
      key: 'name', 
      label: 'API Key Name',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">{value}</Text>
          <Text fontSize="xs" color="gray.500">ID: #{row.id}</Text>
        </VStack>
      )
    },
    { 
      key: 'masked_api_key', 
      label: 'API Key',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Text fontFamily="monospace" fontSize="sm" bg="gray.100" px={2} py={1} borderRadius="md">
            {value}
          </Text>
          <Badge colorScheme={row.mode === 'production' ? 'red' : 'blue'} variant="subtle" fontSize="xs">
            {row.mode}
          </Badge>
        </VStack>
      )
    },
    { 
      key: 'permissions', 
      label: 'Permissions',
      render: (value) => (
        <VStack align="start" spacing={1}>
          {Array.isArray(value) ? value.map((permission, index) => (
            <Badge key={index} colorScheme="purple" variant="subtle" fontSize="xs">
              {permission}
            </Badge>
          )) : (
            <Text fontSize="sm" color="gray.500">No permissions</Text>
          )}
        </VStack>
      )
    },
    { 
      key: 'rate_limit', 
      label: 'Rate Limit',
      render: (value) => (
        <Text fontSize="sm">
          {value ? `${value} req/min` : 'Unlimited'}
        </Text>
      )
    },
    { 
      key: 'created_at', 
      label: 'Created',
      render: (value) => (
        <Text fontSize="sm" color="gray.600">
          {value ? new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : 'N/A'}
        </Text>
      )
    }
  ]

  const fetchApiKeys = async () => {
    if (!tenantId) return

    try {
      const response = await apiClient(`/api-keys/${tenantId}`)

      if (response.status === 'success') {
        const processedApiKeys = response.data.map(key => ({
          ...key,
          masked_api_key: maskApiKey(key.api_key)
        }))
        setApiKeys(processedApiKeys)
        setFilteredApiKeys(processedApiKeys)
      } else {
        throw new Error('Failed to fetch API keys')
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000
      })
    } finally {
      setLoading(false)
    }
  }

  const maskApiKey = (apiKey) => {
    if (!apiKey) return '';
    const length = apiKey.length;
    if (length <= 4) return apiKey;
    
    const lastFour = apiKey.slice(length - 4);
    const maskedPart = '*'.repeat(length - 4);
    return `${maskedPart}${lastFour}`;
  }

  useEffect(() => {
    fetchApiKeys()
  }, [tenantId])

  const handleSearch = (searchTerm) => {
    const lowercaseSearch = searchTerm.toLowerCase()
    const filtered = apiKeys.filter(apiKey =>
      apiKey.name.toLowerCase().includes(lowercaseSearch) ||
      apiKey.mode.toLowerCase().includes(lowercaseSearch) ||
      (apiKey.permissions && apiKey.permissions.some(perm => 
        perm.toLowerCase().includes(lowercaseSearch)
      ))
    )
    setFilteredApiKeys(filtered)
  }

  const handleCreate = () => {
    navigate(`/settings/api-keys/create`)
  }

  const handleEdit = (apiKey) => {
    navigate(`/settings/api-keys/edit/${apiKey.id}`)
  }

  const handleDelete = async (apiKey) => {
    try {
      await apiClient(`/api-keys/${tenantId}/${apiKey.id}`, {
        method: 'DELETE'
      })

      toast({
        title: 'Success',
        description: 'API key deleted successfully',
        status: 'success',
        duration: 3000
      })

      fetchApiKeys()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete API key',
        status: 'error',
        duration: 3000
      })
    }
  }

  // Calculate statistics
  const getApiKeyStats = () => {
    const total = apiKeys.length
    const production = apiKeys.filter(key => key.mode === 'production').length
    const sandbox = apiKeys.filter(key => key.mode === 'sandbox').length
    
    return { total, production, sandbox }
  }

  const stats = getApiKeyStats()

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings">Settings</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>API Keys</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <Heading size="lg" mb={2}>API Keys</Heading>
          <Text color="gray.500">
            Manage your API keys for accessing the CMS API. Create production and sandbox keys with specific permissions.
          </Text>
        </Box>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total API Keys</StatLabel>
                <StatNumber color="blue.500">{stats.total}</StatNumber>
                <StatHelpText>All API keys in your account</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Production Keys</StatLabel>
                <StatNumber color="red.500">{stats.production}</StatNumber>
                <StatHelpText>Live environment access</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Sandbox Keys</StatLabel>
                <StatNumber color="blue.500">{stats.sandbox}</StatNumber>
                <StatHelpText>Development environment</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Enhanced DataTable */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody p={0}>
            <DataTable
              columns={columns}
              data={filteredApiKeys}
              onSearch={handleSearch}
              onEdit={handleEdit}
              onCreate={handleCreate}
              onDelete={handleDelete}
              searchPlaceholder="Search API keys by name, mode, or permissions..."
              isLoading={loading}
              title="API Keys Management"
              createButtonText="Create New API Key"
              emptyStateTitle="No API keys found"
              emptyStateDescription={
                filteredApiKeys.length === 0 && apiKeys.length > 0
                  ? "No API keys match your search criteria. Try adjusting your search terms."
                  : "Get started by creating your first API key to access the CMS API."
              }
              loadingText="Loading API keys..."
            />
          </CardBody>
        </Card>
      </VStack>
    </Container>
  )
}

export default ApiKeys